import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";

const initialState = {
  userId: null,
  userName: null,
  userPoints: 0,
  packId: null,
  packName: null,
  periodId: null,
  daysOrdered: 1,
  selectedDates: [new Date()],
  paymentMethod: null,
  appliedPromoCode: null,
  appliedPromoCodeId: null,
  bonusPointsToUse: 0,
  trigger: 0,
  dishes: [],

  pricingLoading: false,
  payableAmount: 0,
  basePrice: 0,
  bonusPoints: 0,
  finalDaysOrdered: 0,
  additionalDaysToAdd: 0,
  periodDiscountAmount: 0,
  bonusDiscountApplied: 0,
  promoDiscountAmount: 0,

  replacedDishes: [],
  replacedProteinDishes: [],
  replacedSauceDishes: [],

  isCustomMode: false,
  customDishes: {}, // Record<string (YYYY-MM-DD), Dish[]>
};

const createOrderSlice = createSlice({
  name: "createOrderSlice",
  initialState,
  reducers: {
    changeTrigger: (state) => {
      state.trigger += 1;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setUserName: (state, action) => {
      state.userName = action.payload;
    },
    setUserPoints: (state, action) => {
      state.userPoints = action.payload;
    },
    setPackId: (state, action) => {
      state.packId = action.payload;
    },
    setPackName: (state, action) => {
      state.packName = action.payload;
    },
    setDaysOrdered: (state, action) => {
      state.daysOrdered = action.payload;
    },
    setSelectedDates: (state, action) => {
      state.selectedDates = action.payload;
    },
    setPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },
    setAppliedPromoCode: (state, action) => {
      state.appliedPromoCode = action.payload;
    },
    setAppliedPromoCodeId: (state, action) => {
      state.appliedPromoCodeId = action.payload;
    },
    setPeriodId: (state, action) => {
      state.periodId = action.payload;
    },
    setBonusPointsToUse: (state, action) => {
      state.bonusPointsToUse = action.payload;
    },
    setPricingLoading: (state, action) => {
      state.pricingLoading = action.payload;
    },
    setPricing: (state, action) => {
      state.payableAmount = action.payload.payableAmount;
      state.basePrice = action.payload.basePrice;
      state.bonusPoints = action.payload.bonusPoints;
      state.finalDaysOrdered = action.payload.finalDaysOrdered;
      state.additionalDaysToAdd = action.payload.additionalDaysToAdd;
      state.periodDiscountAmount = action.payload.periodDiscountAmount;
      state.bonusDiscountApplied = action.payload.bonusDiscountApplied;
      state.promoDiscountAmount = action.payload.promoDiscountAmount;
      state.dishes = action.payload.dishes;
    },
    setDishes: (state, action) => {
      state.dishes = action.payload;
    },
    setReplacedDishes: (state, action) => {
      state.replacedDishes = action.payload;
    },
    setOneReplacedDish: (state, action) => {
      // check if the dish is already replaced
      const isAlreadyReplaced = state.replacedDishes.find(
        (product) =>
          product.mealId === action.payload.mealId &&
          product.date === action.payload.date &&
          product.packId === action.payload.packId
      );
      if (isAlreadyReplaced) {
        state.replacedDishes = state.replacedDishes.map((product) =>
          product.mealId === action.payload.mealId &&
          product.date === action.payload.date &&
          product.packId === action.payload.packId
            ? action.payload
            : product
        );
      } else {
        state.replacedDishes = [...state.replacedDishes, action.payload];
      }
    },
    removeOneReplacedDish: (state, action) => {
      state.replacedDishes = state.replacedDishes.filter(
        (product) =>
          !(
            product.mealId === action.payload.mealId &&
            product.packId === action.payload.packId &&
            product.date === action.payload.date
          )
      );
    },
    setOneReplacedProteinDish: (state, action) => {
      const existing = state.replacedProteinDishes.find(
        (item) =>
          item.mealId === action.payload.mealId &&
          item.date === action.payload.date &&
          item.packId === action.payload.packId
      );
      if (existing) {
        state.replacedProteinDishes = state.replacedProteinDishes.map((item) =>
          item.mealId === action.payload.mealId &&
          item.date === action.payload.date &&
          item.packId === action.payload.packId
            ? action.payload
            : item
        );
      } else {
        state.replacedProteinDishes = [...state.replacedProteinDishes, action.payload];
      }
    },
    setOneReplacedSauceDish: (state, action) => {
      const existing = state.replacedSauceDishes.find(
        (item) =>
          item.mealId === action.payload.mealId &&
          item.date === action.payload.date &&
          item.packId === action.payload.packId
      );
      if (existing) {
        state.replacedSauceDishes = state.replacedSauceDishes.map((item) =>
          item.mealId === action.payload.mealId &&
          item.date === action.payload.date &&
          item.packId === action.payload.packId
            ? action.payload
            : item
        );
      } else {
        state.replacedSauceDishes = [...state.replacedSauceDishes, action.payload];
      }
    },
    setIsCustomMode: (state, action) => {
      state.isCustomMode = action.payload;
    },
    addCustomDish: (state, action) => {
      const { date, dish } = action.payload;
      if (!state.customDishes[date]) {
        state.customDishes[date] = [];
      }
      state.customDishes[date].push(dish);
    },
    removeCustomDish: (state, action) => {
      const { date, index } = action.payload;
      if (state.customDishes[date]) {
        state.customDishes[date].splice(index, 1);
        if (state.customDishes[date].length === 0) {
          delete state.customDishes[date];
        }
      }
    },
    removeCustomDishGroup: (state, action) => {
      const { date, indices } = action.payload;
      if (state.customDishes[date]) {
        // Sort descending to avoid index shift issues
        const sorted = [...indices].sort((a, b) => b - a);
        sorted.forEach((idx) => state.customDishes[date].splice(idx, 1));
        if (state.customDishes[date].length === 0) {
          delete state.customDishes[date];
        }
      }
    },
    insertCustomDishAt: (state, action) => {
      const { date, dish, index } = action.payload;
      if (!state.customDishes[date]) {
        state.customDishes[date] = [];
      }
      state.customDishes[date].splice(index, 0, dish);
    },
    clearCustomDishes: (state) => {
      state.customDishes = {};
    },
    resetCreateOrder: (state) => {
      state.userId = null;
      state.userName = null;
      state.userPoints = 0;
      state.packId = null;
      state.packName = null;
      state.periodId = null;
      state.daysOrdered = 1;
      state.selectedDates = [new Date()];
      state.paymentMethod = null;
      state.appliedPromoCode = null;
      state.appliedPromoCodeId = null;
      state.bonusPointsToUse = 0;
      state.pricingLoading = false;
      state.payableAmount = 0;
      state.basePrice = 0;
      state.bonusPoints = 0;
      state.finalDaysOrdered = 0;
      state.additionalDaysToAdd = 0;
      state.periodDiscountAmount = 0;
      state.bonusDiscountApplied = 0;
      state.promoDiscountAmount = 0;
      state.dishes = [];
      state.replacedDishes = [];
      state.replacedProteinDishes = [];
      state.replacedSauceDishes = [];
      state.trigger = 0;
      state.isCustomMode = false;
      state.customDishes = {};
    },
  },
});

const { reducer: createOrderReducer, actions } = createOrderSlice;

const createOrderActions = {
  ...actions,
};

export { createOrderReducer, createOrderActions };
